import { useCanWriteWorkspaceAsset } from '@vizit/access';
import type { AssetClass, CollectionImageView } from '@vizit/api';
import { useAssetFavorites, useImageTags, useSetAssetFavoriteMutation } from '@vizit/api';
import { useShowActionsBar, useShowComparison, useShowFavorites, useShowTagging } from '@vizit/feature-flags';
import { FilterLinesIcon, HeartIcon } from '@vizit/icons';
import { Asset, LibraryEmptyState } from '@vizit/molecules';
import { AssetGrid, DeleteImagesDialog, RemoveImageFromCollectionDialog } from '@vizit/organisms';
import { type Heatmap, heatmapLabel } from '@vizit/organisms';
import * as React from 'react';
import { useCollectionDownload } from '../../hooks/use-collection-download';
import { CollectionBulkActionBar } from './collection-asset-grid.bulk-action-bar';
import { useCollectionAssetGrid } from './collection-asset-grid.hooks';
import { useRemoveAssetFromCollection } from './collection-asset-grid.remove-asset.hook';
import { getOnScreenAssetIds, useAssetSelection, useOnScreenAssetIds } from './collection-asset-grid.selection';
import { CollectionAsset } from './collection-asset-grid.sortable-asset';
import { DEFAULT_ASSET_SORT, findUnscoredImages } from './collection-asset-grid.state';
import type { CollectionAssetSortOption, CollectionGridScoring } from './collection-asset-grid.state';
import type { CollectionImageScoreField, UnscoredImagesReport } from './collection-asset-grid.state';

interface CollectionAssetGridProps {
  /**
   * The ID of the portfolio
   */
  workspaceId: string;
  /**
   * The ID of the collection
   */
  collectionId: string;
  /**
   * The collection's name, for the download toasts ("Bundling 5 images from {name}…")
   *
   * @optional
   * @default 'this collection'
   */
  collectionName?: string;
  /**
   * Additional CSS classes to apply to the grid container
   */
  className?: string;
  /**
   * Callback when an asset is clicked
   */
  onNavigateToAsset: (id: string) => void;
  /**
   * Callback when total count changes (useful for displaying count in parent)
   * @param count - The total number of assets in the collection
   */
  onTotalCountChange?: (count: number) => void;
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
  /**
   * Number of items to load per page
   * @default 12
   */
  limit?: number;
  /**
   * How to sort assets within the collection
   * @default CollectionAssetSortOption.DateNewest
   */
  sortOption?: CollectionAssetSortOption;
  /**
   * Which score field score-sort options use (e.g. `vizit_score` or `hero_score`)
   * @default 'vizit_score'
   */
  scoreField?: CollectionImageScoreField;
  /**
   * When true, generated (Spark) images are hidden
   * @default false
   */
  hideSparkImages?: boolean;
  /**
   * When true, only favorited assets are shown. Applied client-side against the served
   * `favorite` flag: the collection images endpoint has no `favorite` query param.
   * @default false
   */
  favoritesOnly?: boolean;
  /**
   * When set, only images of this asset class are shown; `undefined` shows all classes
   */
  classification?: AssetClass;
  tagIds?: string[];
  /**
   * Fixed images per row, e.g. from the thumbnail size slider. `undefined` keeps the responsive columns
   */
  columns?: number;
  /**
   * Multi-select with checkboxes and the action bar (BLU-348). Selection starts over when the collection, filters or
   * sort change (SDD D59)
   * @default false
   */
  isSelectable?: boolean;
  /**
   * Called when the selection starts or ends, so the page can make room for the action bar (e.g. hide the thumbnail
   * slider that sits in the same bottom area)
   */
  onSelectionModeChange?: (isSelectionMode: boolean) => void;
  /**
   * The (benchmark, product category) pair whose scores the tiles show, picked on the page. Tiles then show that
   * pair's score and dim while its scoring runs; switching benchmark skeleton-loads the grid. Unset, the tiles keep
   * the collection's own scores; with a custom benchmark but no pair under it, they show none
   */
  scoring?: CollectionGridScoring;
  /**
   * Called with the images on screen the pair settled without scoring, the tiles reading "Unscored", and that pair, as
   * they change; with `null` while there is no pair
   */
  onUnscoredImagesChange?: (report: UnscoredImagesReport | null) => void;
  heatmap?: Heatmap;
  onCompare?: (imageIds: string[]) => void;
  onExploreTrends?: (imageId: string) => void;
}

/**
 * CollectionAssetGrid component that fetches and displays assets within a collection
 * Uses infinite loading pattern for better performance
 *
 * @param props - The component props
 */
const CollectionAssetGrid = React.memo(function CollectionAssetGrid(props: CollectionAssetGridProps) {
  const {
    workspaceId,
    collectionId,
    collectionName = 'this collection',
    className,
    onNavigateToAsset,
    onTotalCountChange,
    useOptimalInitialLoad = true,
    limit = 12,
    sortOption = DEFAULT_ASSET_SORT,
    scoreField,
    hideSparkImages = false,
    favoritesOnly: favoritesOnlyProp = false,
    classification,
    tagIds: tagIdsProp,
    columns,
    isSelectable = false,
    onSelectionModeChange,
    scoring,
    onUnscoredImagesChange,
    heatmap = 'original',
    onCompare,
    onExploreTrends,
  } = props;

  const showTagging = useShowTagging();
  const showComparison = useShowComparison();
  const { tagsOf } = useImageTags();
  const tagIds = showTagging && tagIdsProp?.length ? tagIdsProp : undefined;

  const {
    assets,
    totalCount,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    sentinelRef,
    loadingCount,
    refreshExpiredArtifactUrls,
  } = useCollectionAssetGrid({
    workspaceId,
    collectionId,
    limit,
    useOptimalInitialLoad,
    sortOption,
    scoreField,
    hideSparkImages,
    classification,
    tagIds,
    columns,
    scoring,
    withArtifacts: showComparison,
  });

  const { isFavorite } = useAssetFavorites();
  const { mutate: setFavorite } = useSetAssetFavoriteMutation();
  const canWrite = useCanWriteWorkspaceAsset();
  const showFavorites = useShowFavorites();
  const showActionsBar = useShowActionsBar();
  // With favourites off, a bookmarked `?fav=only` would filter the grid to
  // nothing with no toggle left to clear it. Neutralize it here so every
  // derived value below — visibleAssets, visibleCount, the scanning and
  // empty-filter states — falls out correct.
  const favoritesOnly = showFavorites ? favoritesOnlyProp : false;

  // A write is addressed at the asset's own workspace, not the one it was
  // reached through -- the backend authorizes on the row's workspace and 404s
  // otherwise. Older payloads may omit it, so fall back to the route's.
  const workspaceIdOf = React.useCallback(
    (asset: CollectionImageView) => asset.workspace_id ?? workspaceId,
    [workspaceId],
  );

  const heatmapOverlayOf = (asset: CollectionImageView) => {
    const src = heatmap === 'original' ? undefined : asset.artifacts?.[heatmap];
    return src
      ? { src, alt: `${heatmapLabel(heatmap)} of ${asset.name}`, onError: refreshExpiredArtifactUrls }
      : undefined;
  };

  const handleToggleFavorite = React.useCallback(
    (asset: CollectionImageView) => {
      const assetWorkspaceId = workspaceIdOf(asset);
      if (!assetWorkspaceId) return;
      setFavorite({ workspace_id: assetWorkspaceId, image_id: asset.id, is_favorite: !isFavorite(asset) });
    },
    [setFavorite, workspaceIdOf, isFavorite],
  );

  // Download from a tile (GRE-344) and from the action bar: the same zip of the collection, named in the toasts
  const { downloadImages } = useCollectionDownload();
  const handleDownload = React.useCallback(
    (imageIds: string[]) => void downloadImages({ workspaceId, collectionId, collectionName, imageIds }),
    [downloadImages, workspaceId, collectionId, collectionName],
  );

  const visibleAssets = React.useMemo(
    () => (favoritesOnly ? assets.filter((asset: CollectionImageView) => isFavorite(asset)) : assets),
    [assets, favoritesOnly, isFavorite],
  );

  // Checkboxes and the bar show only when the bar has a real action, not just "Select visible" (SDD D71). Delete is
  // always there, so with selection on the bar always has one
  const isSelectionEnabled = isSelectable;
  // The bar unmounts with focus inside after a delete, so focus goes back to the grid instead of the page body
  const gridRef = React.useRef<HTMLDivElement>(null);
  const focusGrid = React.useCallback(() => gridRef.current?.focus(), []);

  const visibleIds = React.useMemo(() => visibleAssets.map((asset: CollectionImageView) => asset.id), [visibleAssets]);
  const selection = useAssetSelection({
    orderedIds: visibleIds,
    resetKey: [collectionId, sortOption, scoreField, hideSparkImages, favoritesOnly, classification, tagIds].join('|'),
  });
  const selectedAssets = React.useMemo(
    () => visibleAssets.filter((asset: CollectionImageView) => selection.selectedIds.has(asset.id)),
    [visibleAssets, selection.selectedIds],
  );
  const isSelectionMode = selectedAssets.length > 0;
  const onScreenIds = useOnScreenAssetIds(gridRef, { isEnabled: isSelectionMode, contentKey: visibleIds });

  React.useEffect(() => {
    onSelectionModeChange?.(isSelectionMode);
  }, [isSelectionMode, onSelectionModeChange]);

  const { isOpen, imageIdToRemove, onOpenChange, onRemoveImageDialogOpen, onRemove } = useRemoveAssetFromCollection({
    workspaceId,
    collectionId,
  });
  const removeImage = React.useCallback(
    () => (imageIdToRemove ? onRemove(imageIdToRemove) : Promise.resolve()),
    [imageIdToRemove, onRemove],
  );

  // With the favorites filter on the server's total describes the unfiltered
  // collection, so it would caption a grid of two tiles "12 assets". Only swap
  // in the filtered count once every page has arrived: the filter runs over
  // fetched pages, so counting mid-scroll would show a number that climbs.
  const visibleCount = favoritesOnly && !hasNextPage ? visibleAssets.length : totalCount;

  React.useEffect(() => {
    onTotalCountChange?.(visibleCount);
  }, [visibleCount, onTotalCountChange]);

  const unscoredImages = React.useMemo(() => findUnscoredImages(visibleAssets, scoring), [visibleAssets, scoring]);
  React.useEffect(() => {
    onUnscoredImagesChange?.(unscoredImages);
  }, [unscoredImages, onUnscoredImagesChange]);

  // The filter runs over fetched pages, so an empty filtered view with pages
  // still to come is mid-scan, not a miss: the scroll sentinel keeps pulling
  // until a match appears or the list runs out.
  const isScanningForMatches = favoritesOnly && visibleAssets.length === 0 && hasNextPage;

  // `AssetGrid` renders its "collection is empty, upload something" state
  // whenever it has no children, which would misreport a filter that simply
  // matched nothing. Hold it back until there are no more pages to arrive,
  // mirroring the assets drawer.
  const isTagFiltered = !!tagIds;
  const hasNoFilterMatches =
    (favoritesOnly || isTagFiltered) && visibleAssets.length === 0 && !isLoading && !hasNextPage;

  if (hasNoFilterMatches) {
    return (
      // Takes focus too: deleting every image the filter shows lands here, not on the grid wrapper below
      <div ref={gridRef} tabIndex={-1} className="flex w-full flex-grow items-center justify-center outline-none">
        <LibraryEmptyState
          heading="No assets match your filters."
          description={
            isTagFiltered
              ? 'Try removing a filter, or tag an image from its tile.'
              : 'Try turning the favorites filter off, or favorite an asset from its tile.'
          }
          icon={isTagFiltered ? FilterLinesIcon : HeartIcon}
        />
      </div>
    );
  }

  return (
    <>
      {/* Takes focus after a bulk delete, the bar that had it is gone. A wrapper, so it's there with the empty state
          too when every image was deleted */}
      <div ref={gridRef} tabIndex={-1} className="outline-none">
        <AssetGrid
          showCount={false}
          loadingCount={loadingCount || limit}
          totalCount={visibleCount}
          isLoading={isLoading || isScanningForMatches}
          className={className}
          columns={columns}
        >
          {visibleAssets.map((asset: CollectionImageView) => (
            <CollectionAsset
              key={asset.id}
              image={asset}
              pair={scoring?.pair}
              isAwaitingUploadScore={!!scoring?.waitingImageIds?.includes(asset.id)}
              pairRequestedAt={scoring?.requestedAt}
              isCustomBenchmark={scoring?.isCustomBenchmark}
              needsCategory={scoring?.needsCategory}
              onAssetClick={onNavigateToAsset}
              onRemoveImageDialogOpen={
                !showActionsBar || canWrite({ workspace_id: workspaceIdOf(asset) })
                  ? onRemoveImageDialogOpen
                  : undefined
              }
              isFavorite={isFavorite(asset)}
              isFavoriteDisabled={!workspaceIdOf(asset) || !canWrite({ workspace_id: workspaceIdOf(asset) })}
              onToggleFavorite={showFavorites ? () => handleToggleFavorite(asset) : undefined}
              // A zip with the one image, through the collection download (SDD D53)
              onDownload={showActionsBar ? () => handleDownload([asset.id]) : undefined}
              tagging={
                showTagging && workspaceIdOf(asset)
                  ? { workspaceId: workspaceIdOf(asset), tags: tagsOf(asset) }
                  : undefined
              }
              heatmapOverlay={heatmapOverlayOf(asset)}
              columns={columns}
              selection={
                isSelectionEnabled
                  ? {
                      isSelected: selection.isSelected(asset.id),
                      isSelectionMode,
                      onSelectedChange: ({ shiftKey }) => selection.toggle(asset.id, { shiftKey }),
                    }
                  : undefined
              }
            />
          ))}

          {/* Show loading assets when fetching next page */}
          {isFetchingNextPage && loadingCount
            ? Array.from({ length: loadingCount }, (_, index) => {
                const skeletonId = `collection-asset-skeleton-next-${index}`;
                return (
                  <Asset key={skeletonId} isLoading showScoreBar>
                    <Asset.Image name="Loading..." src={null} />
                  </Asset>
                );
              })
            : null}
        </AssetGrid>
      </div>

      {/* Sentinel element for infinite scroll detection */}
      {hasNextPage && <div ref={sentinelRef} className="h-4 w-full" aria-label="Loading more assets" />}

      {isSelectionEnabled && (
        <CollectionBulkActionBar
          selectedAssets={selectedAssets}
          isOnScreenSelected={selection.isEverySelected(onScreenIds)}
          workspaceId={workspaceId}
          collectionId={collectionId}
          onSelectVisible={() => selection.selectVisible(getOnScreenAssetIds(gridRef.current))}
          onCompare={onCompare ? () => onCompare([...selection.selectedIds]) : undefined}
          onExploreTrends={
            onExploreTrends
              ? () => {
                  const [imageId] = selection.selectedIds;
                  if (imageId) onExploreTrends(imageId);
                }
              : undefined
          }
          onClear={selection.clear}
          showFavorite={showFavorites}
          onDownload={handleDownload}
          onDone={focusGrid}
        />
      )}

      {/* With the Actions Bar on, the same dialog as the bulk Delete: deleting one image is just as permanent (SDD D55,
          D79). Off, the dialog prod has today */}
      {showActionsBar ? (
        <DeleteImagesDialog isOpen={isOpen} onOpenChange={onOpenChange} count={1} onConfirm={removeImage} />
      ) : (
        <RemoveImageFromCollectionDialog
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          onRemove={onRemove}
          imageId={imageIdToRemove}
        />
      )}
    </>
  );
});

export { CollectionAssetGrid };
export type { CollectionAssetGridProps };
